import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { MarketCustomColumn } from '../../types/database'

interface ColumnConfig {
  id: string
  label: string
}

interface ColumnVisibilityDropdownProps {
  hiddenColumns: Set<string>
  onToggleColumn: (columnId: string) => void
  onResetToDefaults: () => void
  customColumns?: MarketCustomColumn[]
  showCellBorders?: boolean
  onToggleCellBorders?: () => void
  historicalMonths?: number
  onHistoricalMonthsChange?: (months: number) => void
}

// Base columns that can be hidden (in display order)
const BASE_COLUMNS: ColumnConfig[] = [
  { id: 'serialNumber', label: 'S/N' },
  { id: 'owner', label: 'Owner' },
  { id: 'broker', label: 'Broker' },
  { id: 'yearMfr', label: 'MAN Date / EIS Date' },
  { id: 'aftt', label: 'AFTT' },
  { id: 'emp', label: 'EMP' },
  { id: 'dmg', label: 'DMG' },
  { id: 'pax', label: '# PAX' },
  { id: 'paintInterior', label: 'Paint / Interior' },
  { id: 'options', label: 'Options' },
  { id: 'dateListed', label: 'Date Listed' },
  { id: 'jnAsk', label: 'JN Ask' },
  { id: 'askingPrice', label: 'Asking / Take' },
  { id: 'externalColumns', label: 'Adjusted Ask / Take' },
  { id: 'relativeValue', label: 'Relative Value' },
]

export default function ColumnVisibilityDropdown({
  hiddenColumns,
  onToggleColumn,
  onResetToDefaults,
  customColumns = [],
  showCellBorders = true,
  onToggleCellBorders,
  historicalMonths = 12,
  onHistoricalMonthsChange
}: ColumnVisibilityDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 })

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8, // 8px gap below button
        right: window.innerWidth - rect.right
      })
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Build full column list including custom columns
  const allColumns: ColumnConfig[] = [
    ...BASE_COLUMNS,
    ...customColumns.map(col => ({
      id: `custom${col.column_index}`,
      label: col.column_title
    }))
  ]

  const hiddenCount = hiddenColumns.size

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="fixed w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[9999]"
      style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
    >
      <div className="py-1" role="menu" aria-orientation="vertical">
        {/* Reset to Defaults button */}
        <div className="px-4 py-2 border-b border-gray-100">
          <button
            type="button"
            onClick={() => {
              onResetToDefaults()
            }}
            className="w-full text-left text-sm text-purple-600 hover:text-purple-800 font-medium"
          >
            ↻ Reset to Defaults (Show All)
          </button>
        </div>

        {/* Cell Borders toggle */}
        {onToggleCellBorders && (
          <div className="px-4 py-2 border-b border-gray-100">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showCellBorders}
                onChange={onToggleCellBorders}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-3 text-sm text-gray-700">Cell Borders</span>
            </label>
          </div>
        )}

        {/* Historical Data Range slider */}
        {onHistoricalMonthsChange && (
          <div className="px-4 py-3 border-b border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Historical Data Range
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="1"
                max="48"
                value={historicalMonths}
                onChange={(e) => onHistoricalMonthsChange(parseInt(e.target.value, 10))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <span className="text-sm text-gray-700 min-w-[4rem] text-right">
                {historicalMonths} {historicalMonths === 1 ? 'month' : 'months'}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Filter Sold &amp; Removed tables
            </p>
          </div>
        )}

        {/* Column list */}
        <div className="max-h-80 overflow-y-auto">
          {allColumns.map((column) => {
            const isHidden = hiddenColumns.has(column.id)
            return (
              <label
                key={column.id}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!isHidden}
                  onChange={() => onToggleColumn(column.id)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="ml-3">{column.label}</span>
              </label>
            )
          })}
        </div>
      </div>
    </div>
  ) : null

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        Column Visibility
        {hiddenCount > 0 && (
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            {hiddenCount} hidden
          </span>
        )}
        <svg className={`ml-2 w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {createPortal(dropdownContent, document.body)}
    </div>
  )
}

